import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SortMeta } from 'primeng/api';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { SKIP_GLOBAL_ERROR_HANDLER } from '../../../../core/tokens/http-context-tokens';
import { ColumnSort } from '../../../../osp-ui/components/data/table/contract/column-sort.contract';
import { PagedResponse } from '../../../../shared/contract/paged-response.contract';
import { convertSortMetaToColumnSort } from '../../../../shared/utils/convert-sort-meta-to-column-sort';
import { getHttpParameters } from '../../../../shared/utils/get-http-parameters';
import { isSortMeta } from '../../../../shared/utils/is-sort-meta';
import { toSortString } from '../../../../shared/utils/to-sort-string';
import { AutomatedTransfersCounts } from '../contract/automated-transfers-counts.contract';
import { OnTheRoadEquipment } from '../contract/automated-transfers-equipment-on-the-road-view.contract';
import { ScheduledEquipment } from '../contract/automated-transfers-equipment-scheduled-view.contract';
import { AutomatedTransfersFilterCollectionView } from '../contract/automated-transfers-filter-collection-view.contract';
import { AutomatedTransfersOnTheRoadFilterRequestParams } from '../contract/automated-transfers-on-the-road-request-params.contract';
import { AutomatedTransfersScheduledFilterRequestParams } from '../contract/automated-transfers-scheduled-filter-request-params.contract';
import { ScheduledTransfer } from '../contract/automated-transfers-transfer-scheduled-view.contract';
import {
  BulkCancelEquipmentTransferRequest,
  CancelEquipmentTransferRequest,
} from '../contract/cancel-equipment-transfer-request.contract';
import { BulkCancelTransferRequest, CancelTransferRequest } from '../contract/cancel-transfer-request.contract';
import {
  BulkDeactivateEquipmentScheduledTransferRequest,
  DeactivateEquipmentScheduledTransferRequest,
} from '../contract/deactivate-equipment-scheduled-transfer-request.contract';
import {
  BulkDeactivateScheduledTransferRequest,
  DeactivateScheduledTransferRequest,
} from '../contract/deactivate-scheduled-transfer-request.contract';
import { EquipmentAmountInfo } from '../contract/equipment-amount-info.contract';
import {
  BulkPauseEquipmentTransferRequest,
  PauseEquipmentTransferRequest,
} from '../contract/pause-equipment-transfer-request.contract';
import {
  BulkReactivateEquipmentScheduledTransferRequest,
  ReactivateEquipmentScheduledTransferRequest,
} from '../contract/reactivate-equipment-scheduled-transfer-request.contract';
import { ReactivateScheduledTransferRequest } from '../contract/reactivate-scheduled-transfer-request.contract';
import {
  BulkRestartEquipmentTransferRequest,
  RestartEquipmentTransferRequest,
} from '../contract/restart-equipment-transfer-request.contract';
import { SearchAutomatedTransfersOnTheRoadRequest } from '../contract/search-automated-transfers-on-the-road-request.contract';
import { SearchAutomatedTransfersScheduledRequest } from '../contract/search-automated-transfers-scheduled-request.contract';
import { UpdateDeliveryTimeThresholdRequest } from '../contract/update-delivery-time-threshold-request.contract';
import { AutomatedTransferColumnKeys } from '../enums/automated-transfer-column-keys.enum';

@Injectable({ providedIn: 'root' })
export class AutomatedTransfersApiService {
  private readonly http = inject(HttpClient);

  private readonly url = environment.APP_DISPOSITION_SERVICE_BASE_URL;
  private readonly automatedTransfersUrl = `${this.url}/api/v1/automated-transfers`;
  private readonly equipmentAmountUrl = `${this.url}/api/v1/amounts/equipment`;
  private readonly skipGlobalErrorHandler = new HttpContext().set(SKIP_GLOBAL_ERROR_HANDLER, true);

  public getEquipmentsOnTheRoad(
    request: SearchAutomatedTransfersOnTheRoadRequest,
  ): Observable<PagedResponse<OnTheRoadEquipment>> {
    const httpParams = this.appendSortParam(getHttpParameters(request), request.sort, 'onTheRoad');

    return this.http.get<PagedResponse<OnTheRoadEquipment>>(`${this.automatedTransfersUrl}/equipments/on-the-road`, {
      params: httpParams,
    });
  }

  public getFiltersOnTheRoad(
    params: AutomatedTransfersOnTheRoadFilterRequestParams,
  ): Observable<AutomatedTransfersFilterCollectionView> {
    const httpParams = getHttpParameters(params);
    return this.http.get<AutomatedTransfersFilterCollectionView>(
      `${this.automatedTransfersUrl}/equipments/on-the-road/filter`,
      { params: httpParams },
    );
  }

  public pauseTransfer(request: PauseEquipmentTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/pause-equipment-on-the-road`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public bulkPauseTransfer(request: BulkPauseEquipmentTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/bulk-pause-equipment-on-the-road`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public cancelTransfer(request: CancelTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/cancel-scheduled-transfer`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public bulkCancelTransfer(request: BulkCancelTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/bulk-cancel-scheduled-transfer`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public cancelEquipmentTransfer(request: CancelEquipmentTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/cancel-single-equipment-transfer`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public bulkCancelEquipmentTransfer(request: BulkCancelEquipmentTransferRequest): Observable<string> {
    return this.http.post(
      `${this.automatedTransfersUrl}/bulk-cancel-single-equipment-transfer`,
      JSON.stringify(request),
      { responseType: 'text', context: this.skipGlobalErrorHandler },
    );
  }

  public deactivateScheduledTransfer(request: DeactivateScheduledTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/deactivate-scheduled-transfer`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }
  public bulkDeactivateScheduledTransfer(request: BulkDeactivateScheduledTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/bulk-deactivate-scheduled-transfer`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public deactivateEquipmentScheduledTransfer(
    request: DeactivateEquipmentScheduledTransferRequest,
  ): Observable<string> {
    return this.http.post(
      `${this.automatedTransfersUrl}/deactivate-equipment-scheduled-transfer`,
      JSON.stringify(request),
      { responseType: 'text', context: this.skipGlobalErrorHandler },
    );
  }

  public bulkDeactivateEquipmentScheduledTransfer(
    request: BulkDeactivateEquipmentScheduledTransferRequest,
  ): Observable<string> {
    return this.http.post(
      `${this.automatedTransfersUrl}/bulk-deactivate-equipment-scheduled-transfer`,
      JSON.stringify(request),
      { responseType: 'text', context: this.skipGlobalErrorHandler },
    );
  }

  public reactivateScheduledTransfer(request: ReactivateScheduledTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/reactivate-scheduled-transfer`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public reactivateEquipmentScheduledTransfer(
    request: ReactivateEquipmentScheduledTransferRequest,
  ): Observable<string> {
    return this.http.post(
      `${this.automatedTransfersUrl}/reactivate-equipment-scheduled-transfer`,
      JSON.stringify(request),
      { responseType: 'text', context: this.skipGlobalErrorHandler },
    );
  }

  public bulkReactivateEquipmentScheduledTransfer(
    request: BulkReactivateEquipmentScheduledTransferRequest,
  ): Observable<string> {
    return this.http.post(
      `${this.automatedTransfersUrl}/bulk-reactivate-equipment-scheduled-transfer`,
      JSON.stringify(request),
      {
        responseType: 'text',
        context: this.skipGlobalErrorHandler,
      },
    );
  }

  public restartTransfer(request: RestartEquipmentTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/restart-equipment-on-the-road`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public bulkRestartTransfer(request: BulkRestartEquipmentTransferRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/bulk-restart-equipment-on-the-road`, JSON.stringify(request), {
      responseType: 'text',
      context: this.skipGlobalErrorHandler,
    });
  }

  public getDeliveryTimeThreshold(): Observable<number> {
    return this.http.get<number>(`${this.automatedTransfersUrl}/delivery-time-threshold`);
  }

  public saveCustomDeliveryOffset(request: UpdateDeliveryTimeThresholdRequest): Observable<string> {
    return this.http.post(`${this.automatedTransfersUrl}/update-delivery-time-threshold`, JSON.stringify(request), {
      responseType: 'text',
    });
  }

  public getTransfersScheduled(
    request: SearchAutomatedTransfersScheduledRequest,
  ): Observable<PagedResponse<ScheduledTransfer>> {
    const httpParams = this.appendSortParam(getHttpParameters(request), request.sort, 'scheduledTransfer');

    return this.http.get<PagedResponse<ScheduledTransfer>>(`${this.automatedTransfersUrl}/scheduled`, {
      params: httpParams,
    });
  }

  public getEquipmentsScheduled(
    request: SearchAutomatedTransfersScheduledRequest,
    transferId: string,
  ): Observable<PagedResponse<ScheduledEquipment>> {
    const httpParams = this.appendSortParam(getHttpParameters(request), request.sort, 'scheduledEquipment');

    return this.http.get<PagedResponse<ScheduledEquipment>>(`${this.automatedTransfersUrl}/scheduled/${transferId}`, {
      params: httpParams,
    });
  }

  public getFiltersScheduled(
    params: AutomatedTransfersScheduledFilterRequestParams,
  ): Observable<AutomatedTransfersFilterCollectionView> {
    const httpParams = getHttpParameters(params);
    return this.http.get<AutomatedTransfersFilterCollectionView>(`${this.automatedTransfersUrl}/scheduled/filters`, {
      params: httpParams,
    });
  }

  public getEquipmentAmountInfo(equipmentId: string): Observable<EquipmentAmountInfo> {
    return this.http.get<EquipmentAmountInfo>(`${this.equipmentAmountUrl}/${equipmentId}`);
  }

  public getAutomatedTransfersCounts(): Observable<AutomatedTransfersCounts> {
    return this.http.get<AutomatedTransfersCounts>(`${this.automatedTransfersUrl}/count`);
  }

  private appendSortParam(
    httpParams: HttpParams,
    sort: ColumnSort | SortMeta | null,
    context: 'onTheRoad' | 'scheduledTransfer' | 'scheduledEquipment',
  ): HttpParams {
    const normalizedSort = this.normalizeSort(sort);
    if (!normalizedSort) return httpParams;

    let sortParams: string[];

    switch (context) {
      case 'onTheRoad':
        sortParams = this.mapSortOnTheRoad(normalizedSort);
        break;
      case 'scheduledTransfer':
        sortParams = this.mapSortScheduledTransfer(normalizedSort);
        break;
      case 'scheduledEquipment':
      default:
        sortParams = this.toSortArray(normalizedSort);
        break;
    }

    httpParams = httpParams.delete('sort');
    sortParams.forEach((sortParam) => (httpParams = httpParams.append('sort', sortParam)));
    return httpParams;
  }

  private normalizeSort(sort: ColumnSort | SortMeta | null): ColumnSort | null {
    if (!sort) return null;
    return isSortMeta(sort) ? convertSortMetaToColumnSort(sort) : sort;
  }

  private mapSortOnTheRoad(sort: ColumnSort): string[] {
    const field = sort.field as AutomatedTransferColumnKeys;

    switch (field) {
      case AutomatedTransferColumnKeys.TRANSFER_STATUS:
        return [toSortString({ field: 'transferStatus', direction: sort.direction })];
      case AutomatedTransferColumnKeys.SOURCE_LOCATION_NAME:
        return [toSortString({ field: 'leftLocationName', direction: sort.direction })];
      case AutomatedTransferColumnKeys.DURATION_SINCE_LEAVING_SOURCE_LOCATION:
      case AutomatedTransferColumnKeys.TIMESTAMP_SOURCE_GEOFENCE_LEFT:
        return [toSortString({ field: 'leavingTimestamp', direction: sort.direction })];
      default:
        return this.toSortArray(sort);
    }
  }

  private mapSortScheduledTransfer(sort: ColumnSort): string[] {
    const field = sort.field;

    if (field === 'transferType') {
      return [
        toSortString({ field: 'sourceLocationType', direction: sort.direction }),
        toSortString({ field: 'targetLocationType', direction: sort.direction }),
      ];
    }

    return this.toSortArray(sort);
  }

  private toSortArray(sort: ColumnSort): string[] {
    return [toSortString(sort)];
  }
}
